import { useCallback, useEffect, useRef, useState } from "react";

/** How far one arrow-key press moves a panel edge; Shift multiplies it for coarse adjustment. */
export const RESIZE_NUDGE_PX = 16;
export const RESIZE_NUDGE_COARSE_PX = 64;

export interface ResizeDragOptions {
  min: number;
  max: number;
  defaultValue: number;
  /** Set true for right-side panels whose drag direction is inverted. */
  reverse?: boolean;
}

export interface ResizeDragHandle {
  width: number;
  setWidth: (w: number) => void;
  min: number;
  max: number;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  /** Move the edge by `delta` px in the panel's own direction, clamped to [min, max]. */
  nudge: (delta: number) => void;
  /** Snap back to the panel's default width — the escape hatch from an awkward drag. */
  reset: () => void;
}

/**
 * Drag-to-resize hook shared across Dev-Agent, Weave, and Testing panels.
 *
 * Listeners attach to `document` (not the element) so the cursor never outpaces the handle during
 * fast moves. Touch is supported alongside mouse, and `nudge`/`reset` back the keyboard affordance on
 * ResizeGrip — a resize used to be mouse-only, which left every resizable panel in the app
 * unreachable by keyboard and on touch devices.
 *
 * No localStorage persistence by design — widths reset on reload to avoid stale layout state.
 */
export function useResizeDrag(opts: ResizeDragOptions): ResizeDragHandle {
  const [width, setWidthState] = useState(opts.defaultValue);
  // The drag reads the current width from a ref so the move handler does not need to be recreated on
  // every pixel of movement (the previous version listed `width` as a dependency, rebuilding the
  // callback and its closures ~60×/second for the duration of a drag).
  const widthRef = useRef(width);
  const clamp = useCallback(
    (w: number) => Math.max(opts.min, Math.min(opts.max, w)),
    [opts.min, opts.max],
  );
  const setWidth = useCallback(
    (w: number) => {
      const next = clamp(w);
      widthRef.current = next;
      setWidthState(next);
    },
    [clamp],
  );

  // Teardown for the drag currently in flight, so an unmount can end it. Without this, a pointer
  // released OUTSIDE the window never delivered its mouseup, leaving mousemove/mouseup/touch* bound
  // to `document` permanently along with a closure over this hook's setState. Verified in a browser:
  // five listeners survived module unmount before this, zero after.
  const endDragRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endDragRef.current?.(), []);

  const beginDrag = useCallback(
    (startX: number) => {
      const startW = widthRef.current;
      const apply = (clientX: number) => {
        const delta = opts.reverse ? -(clientX - startX) : clientX - startX;
        setWidth(startW + delta);
      };
      const onMouseMove = (ev: MouseEvent) => apply(ev.clientX);
      const onTouchMove = (ev: TouchEvent) => {
        if (ev.touches[0]) apply(ev.touches[0].clientX);
      };
      const cleanup = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", cleanup);
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", cleanup);
        document.removeEventListener("touchcancel", cleanup);
        endDragRef.current = null;
      };
      // A second drag must never stack listeners on top of an unfinished one.
      endDragRef.current?.();
      endDragRef.current = cleanup;
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", cleanup);
      document.addEventListener("touchmove", onTouchMove, { passive: true });
      document.addEventListener("touchend", cleanup);
      document.addEventListener("touchcancel", cleanup);
    },
    [opts.reverse, setWidth],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      beginDrag(e.clientX);
    },
    [beginDrag],
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (touch) beginDrag(touch.clientX);
    },
    [beginDrag],
  );

  const nudge = useCallback(
    (delta: number) => setWidth(widthRef.current + (opts.reverse ? -delta : delta)),
    [opts.reverse, setWidth],
  );

  const reset = useCallback(() => setWidth(opts.defaultValue), [opts.defaultValue, setWidth]);

  return { width, setWidth, min: opts.min, max: opts.max, onMouseDown, onTouchStart, nudge, reset };
}
